import React, { useState, useEffect } from 'react'
import {
  Button,
  Input,
  Table,
  Popconfirm,
  message,
  Card,
  Typography,
  Divider,
  Space,
  Tag,
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  MedicineBoxOutlined,
} from '@ant-design/icons'
import { firestore } from 'utils/firebase'
import {
  collection,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  writeBatch,
} from 'firebase/firestore'

import styles from '../settings.module.scss'

const { Title, Text } = Typography

const ReferralSpecialities = () => {
  const [specialities, setSpecialities] = useState([])
  const [newSpeciality, setNewSpeciality] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchSpecialities()
  }, [])

  const fetchSpecialities = async () => {
    try {
      setLoading(true)
      const specialitiesRef = collection(
        firestore,
        'settings/referralSpecialities/list',
      )
      const snapshot = await getDocs(specialitiesRef)

      if (!snapshot.empty) {
        const formattedData = snapshot.docs.map((doc) => {
          const data = doc.data()
          return {
            id: doc.id,
            value: doc.id.toLowerCase(),
            label: data.label || doc.id,
          }
        })
        setSpecialities(formattedData)
      } else {
        // Initialize with default values if none exist
        const defaults = [
          { id: 'dermatology', value: 'dermatology', label: 'Dermatology' },
          { id: 'cardiology', value: 'cardiology', label: 'Cardiology' },
          { id: 'neurology', value: 'neurology', label: 'Neurology' },
        ]

        const batch = writeBatch(firestore)

        defaults.forEach((item) => {
          const docRef = doc(
            firestore,
            `settings/referralSpecialities/list/${item.id}`,
          )
          batch.set(docRef, {
            label: item.label,
          })
        })

        await batch.commit()
        setSpecialities(defaults)
      }
    } catch (error) {
      console.error('Error fetching specialities:', error)
      message.error('Failed to load specialities')
    } finally {
      setLoading(false)
    }
  }

  const addSpeciality = async () => {
    if (!newSpeciality.trim()) {
      message.warning('Please enter a speciality name')
      return
    }

    try {
      setLoading(true)
      const id = newSpeciality.trim().toLowerCase().replace(/\s+/g, '_')

      // Check if speciality already exists
      if (specialities.some((item) => item.id === id)) {
        message.warning('This speciality already exists')
        return
      }

      // Add new speciality to Firestore
      const docRef = doc(firestore, `settings/referralSpecialities/list/${id}`)
      await setDoc(docRef, {
        label: newSpeciality.trim(),
      })

      // Update local state
      setSpecialities([
        ...specialities,
        {
          id,
          value: id,
          label: newSpeciality.trim(),
        },
      ])
      setNewSpeciality('')
      message.success('Speciality added successfully')
    } catch (error) {
      console.error('Error adding speciality:', error)
      message.error('Failed to add speciality')
    } finally {
      setLoading(false)
    }
  }

  const deleteSpeciality = async (id) => {
    try {
      setLoading(true)
      const docRef = doc(firestore, `settings/referralSpecialities/list/${id}`)
      await deleteDoc(docRef)

      setSpecialities(specialities.filter((item) => item.id !== id))
      message.success('Speciality deleted successfully')
    } catch (error) {
      console.error('Error deleting speciality:', error)
      message.error('Failed to delete speciality')
    } finally {
      setLoading(false)
    }
  }

  // Columns for specialities table
  const columns = [
    {
      title: 'Speciality Name',
      dataIndex: 'label',
      key: 'label',
      render: (text) => (
        <Space>
          <MedicineBoxOutlined style={{ color: '#1890ff' }} />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Popconfirm
          title="Are you sure you want to delete this speciality?"
          description="This action cannot be undone."
          onConfirm={() => deleteSpeciality(record.id)}
          okText="Yes"
          cancelText="No"
          okButtonProps={{ danger: true }}
        >
          <Button type="primary" danger icon={<DeleteOutlined />} size="small">
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ]

  return (
    <Card
      className={styles.sectionContainer}
      bordered={true}
      title={
        <Space>
          <MedicineBoxOutlined />
          <span>Referral Specialities</span>
        </Space>
      }
    >
      <Text type="secondary">
        Manage medical specialities available for patient referrals. These
        specialities will be available as options when creating referral
        letters.
      </Text>

      <Divider />

      <div
        className={styles.addSpecialityContainer}
        style={{ display: 'flex', marginBottom: 20, alignItems: 'center' }}
      >
        <Input
          placeholder="Enter speciality name"
          value={newSpeciality}
          onChange={(e) => setNewSpeciality(e.target.value)}
          onPressEnter={addSpeciality}
          style={{ marginRight: 16, flex: 1, maxWidth: 400 }}
          prefix={<MedicineBoxOutlined style={{ color: '#bfbfbf' }} />}
          size="small"
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={addSpeciality}
          loading={loading}
          size="large"
        >
          Add Speciality
        </Button>
      </div>

      <Table
        dataSource={specialities}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={false}
        className={styles.specialitiesTable}
        bordered
        size="middle"
      />
    </Card>
  )
}

export default ReferralSpecialities
